import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CopyAllRoundedIcon from "@mui/icons-material/CopyAllRounded";
import ExitToAppIcon from "@mui/icons-material/OutputRounded";
import { Button, Grid, Stack } from "@mui/material";
import React, { useCallback } from "react";
import { useSelector } from "react-redux";
import HorizontalFill from "@entrilia/shared/components/HorizontalFill";
import biApi from "../../../../../../../../../api/biApi";
import type { IrrDrillDownInfo } from "../../../../../../../../../api/types/svTypes.ts";
import { useDrillDownExport } from "../../../../../hooks/drillDownExport";
import { selectSessionId } from "../../../../../store/selectors/currentReportSelectors";
import type { DrillDownPage } from "../../../../../store/types/drillDownPageState.types";
import { getActiveLayer } from "../../../../../store/utils/getActiveLayer.ts";

interface Props {
  configuration: DrillDownPage;
  info: IrrDrillDownInfo;
}
export default function ToolBar({ configuration, info }: Props) {
  const sessionId = useSelector(selectSessionId);
  const exporting = useDrillDownExport(biApi.requestCashflowExport);
  const [copied, setCopied] = React.useState(false);

  const copyToClipboard = useCallback(() => {
    if (copied) return;
    const result = info.cashFlowValues
      ?.map((v) => {
        if (v.dateFormatted) {
          const date = new Date(v.date);
          const dateFormatted = !isNaN(date.getTime()) ? date.toLocaleDateString() : "";
          const amountFormatted = v.amount.toLocaleString(undefined, { useGrouping: false });
          return `${v.description}\t${dateFormatted}\t${amountFormatted}`;
        } else {
          return `${v.description}\t\t${v.amountFormatted}`;
        }
      })
      .join("\n");
    if (result) {
      navigator.clipboard.writeText(result);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    }
  }, [copied, info.cashFlowValues]);

  const exportToExcel = useCallback(() => {
    const activeLayer = getActiveLayer(configuration);
    const activeMeasure = activeLayer.measure;
    const caption = activeMeasure?.config.customLabel || activeMeasure?.meta.caption || "Cash Flow";
    exporting.requestExport({ caption, sessionId, itemId: activeLayer.drillDownId || "" });
  }, [configuration, exporting, sessionId]);

  return (
    <Grid container sx={{ alignItems: "center", gap: 1.5, width: "100%" }}>
      <HorizontalFill />
      <Stack direction={"row"} sx={{ gap: 1 }}>
        <Button
          variant="outlined"
          color="secondary"
          startIcon={copied ? <CheckRoundedIcon /> : <CopyAllRoundedIcon />}
          onClick={copyToClipboard}
        >
          {copied ? "Copied" : "Copy Data"}
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          disabled={exporting.exporting}
          startIcon={<ExitToAppIcon />}
          loading={exporting.exporting}
          onClick={exportToExcel}
        >
          Export
        </Button>
      </Stack>
    </Grid>
  );
}
